import request from '#/utils/request';
import { keysToSnake } from '#/api/adapters/wireCase';
import {
  parseVer4DetailResponse,
  parseVer4ListResponse,
} from '#/api/adapters/envelope';
import { listQueryToApiParams } from '#/utils/listUrl/parseListSearchParams';
import { hydrateJsonBlobError } from '#/utils/blobResponseError';

/** multipart：讓瀏覽器帶 boundary（覆蓋 instance 預設 JSON Content-Type） */
const MULTIPART_HEADERS = { 'Content-Type': undefined };

const DEFAULT_LIST_QUERY = { page: 1, pageSize: 50 };

function parseFilenameFromContentDisposition(header) {
  if (!header || typeof header !== 'string') {
    return null;
  }

  const utf8Match = /filename\*=(?:UTF-8''|utf-8'')([^;]+)/i.exec(header);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ''));
    } catch {
      return utf8Match[1].trim().replace(/^"|"$/g, '');
    }
  }

  const plainMatch = /filename="?([^";]+)"?/i.exec(header);
  return plainMatch?.[1]?.trim() || null;
}

async function readBlobResponse(requestPromise, fallbackFilename) {
  try {
    const response = await requestPromise;
    const disposition = response.headers?.['content-disposition']
      ?? response.headers?.['Content-Disposition'];
    const filename = parseFilenameFromContentDisposition(disposition)
      || fallbackFilename;

    return {
      blob: response.data,
      filename,
      contentType: response.headers?.['content-type']
        ?? response.headers?.['Content-Type']
        ?? response.data?.type
        ?? '',
    };
  } catch (error) {
    await hydrateJsonBlobError(error);
    throw error;
  }
}

/**
 * 平台附件 API 工廠。`basePath(businessId)` 回傳如
 * `/srm/purchase-order/{id}/attachments`。
 *
 * @param {(businessId: string) => string} basePath
 * @param {object} [options]
 * @param {Record<string, string>} [options.readParams] 只附加在 GET（列表／預覽／下載）的 query，
 *   例：FMS 由請款單讀關聯憑證附件帶 `payment_request_id`
 */
export function createAttachmentsApi(basePath, { readParams } = {}) {
  async function createUploadBatch(businessId, payload) {
    const { data: raw } = await request.post(
      `${basePath(businessId)}/upload-batches`,
      keysToSnake(payload),
    );
    return parseVer4DetailResponse(raw);
  }

  async function upload(businessId, { uploadBatchId, file, remark }) {
    const formData = new FormData();
    formData.append('upload_batch_id', uploadBatchId);
    formData.append('file', file);
    if (remark != null && remark !== '') {
      formData.append('remark', remark);
    }

    const { data: raw } = await request.post(
      basePath(businessId),
      formData,
      { headers: MULTIPART_HEADERS },
    );
    return parseVer4DetailResponse(raw);
  }

  async function fetchList(businessId, listQuery = DEFAULT_LIST_QUERY) {
    const { data: raw } = await request.get(basePath(businessId), {
      params: { ...listQueryToApiParams(listQuery), ...readParams },
    });
    return parseVer4ListResponse(raw);
  }

  async function unlink(businessId, fileId) {
    const { data: raw } = await request.delete(
      `${basePath(businessId)}/${fileId}`,
    );
    return parseVer4DetailResponse(raw);
  }

  async function fetchPreviewBlob(businessId, fileId, version, fallbackFilename) {
    return readBlobResponse(
      request.get(
        `${basePath(businessId)}/${fileId}/versions/${version}/preview`,
        { responseType: 'blob', params: readParams },
      ),
      fallbackFilename || 'preview',
    );
  }

  async function fetchDownloadBlob(businessId, fileId, version, fallbackFilename) {
    return readBlobResponse(
      request.get(
        `${basePath(businessId)}/${fileId}/versions/${version}/download`,
        { responseType: 'blob', params: readParams },
      ),
      fallbackFilename || 'download',
    );
  }

  return {
    createUploadBatch,
    upload,
    fetchList,
    unlink,
    fetchPreviewBlob,
    fetchDownloadBlob,
  };
}
